let cachedLocation = null;
let cachedPublicIp = null;
let prefetchPromise = null;

function isGeolocationSupported() {
  return typeof navigator !== 'undefined' && typeof navigator.geolocation?.getCurrentPosition === 'function';
}

async function isGeolocationAlreadyGranted() {
  if (!navigator.permissions?.query) {
    return false;
  }
  try {
    const status = await navigator.permissions.query({ name: 'geolocation' });
    return status.state === 'granted';
  } catch {
    return false;
  }
}

function readGrantedDeviceLocation(timeoutMs = 8000) {
  if (!isGeolocationSupported()) {
    return Promise.resolve(null);
  }
  if (cachedLocation) {
    return Promise.resolve({ ...cachedLocation });
  }

  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        cachedLocation = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
        };
        resolve({ ...cachedLocation });
      },
      () => resolve(null),
      {
        enableHighAccuracy: true,
        timeout: timeoutMs,
        maximumAge: 120000,
      },
    );
  });
}

async function fetchPublicIp() {
  if (cachedPublicIp) {
    return cachedPublicIp;
  }
  try {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), 3000);
    const response = await fetch('https://api.ipify.org?format=json', {
      signal: controller.signal,
      cache: 'no-store',
    });
    window.clearTimeout(timer);
    if (!response.ok) {
      return null;
    }
    const data = await response.json();
    const ip = data?.ip ? String(data.ip).trim() : null;
    cachedPublicIp = ip;
    return ip;
  } catch {
    return null;
  }
}

function buildContextFromCache() {
  const context = { agreedToTerms: true };
  if (cachedPublicIp) {
    context.publicIp = cachedPublicIp;
  }
  if (cachedLocation) {
    context.deviceLocation = {
      latitude: cachedLocation.latitude,
      longitude: cachedLocation.longitude,
      accuracy: cachedLocation.accuracy,
    };
  }
  return context;
}

async function collectLocationData({ gpsTimeoutMs = 8000 } = {}) {
  const [publicIp, granted] = await Promise.all([
    fetchPublicIp(),
    isGeolocationAlreadyGranted(),
  ]);

  const context = { agreedToTerms: true };
  if (publicIp) {
    context.publicIp = publicIp;
  }

  if (granted) {
    const location = await readGrantedDeviceLocation(gpsTimeoutMs);
    if (location) {
      context.deviceLocation = {
        latitude: location.latitude,
        longitude: location.longitude,
        accuracy: location.accuracy,
      };
    }
  }

  return context;
}

/**
 * 勾选协议后在后台预取位置（可选优化，不阻塞登录）。
 */
export function prefetchLoginContext() {
  if (!prefetchPromise) {
    prefetchPromise = collectLocationData({ gpsTimeoutMs: 8000 }).finally(() => {
      prefetchPromise = null;
    });
  }
  return prefetchPromise;
}

/**
 * 登录提交时使用：仅标记已同意协议，绝不等待定位。
 */
export function buildLoginContext(agreedToTerms) {
  if (!agreedToTerms) {
    return {};
  }
  prefetchLoginContext();
  return { agreedToTerms: true };
}

async function resolveFullLoginContext() {
  if (prefetchPromise) {
    try {
      await prefetchPromise;
    } catch {
      /* 预取失败则重新采集 */
    }
  }
  const cached = buildContextFromCache();
  if (cached.publicIp || cached.deviceLocation) {
    return cached;
  }
  return collectLocationData({ gpsTimeoutMs: 8000 });
}

/**
 * 登录成功后在后台静默采集并上报位置，不阻塞跳转与功能使用。
 */
export function supplementLoginLocationAfterAuth(recordFn) {
  void (async () => {
    try {
      await recordFn({ agreedToTerms: true });
      const full = await resolveFullLoginContext();
      if (full.publicIp || full.deviceLocation) {
        await recordFn(full);
      }
    } catch {
      /* 位置上报失败不影响登录 */
    }
  })();
}
